import { Link, useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  CheckSquare,
  ClipboardList,
  FileText,
  History,
  Mail,
} from "lucide-react";
import {
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  VButton,
  VTabs,
  VTabsContent,
  VTabsList,
  VTabsTrigger,
} from "@/components/ui";
import { useCase } from "@/domains/cases/api/queries";
import { CaseStatusBadge } from "@/domains/cases/components/caseStatusBadge";
import { CaseOverviewTab } from "@/domains/cases/components/caseOverviewTab";
import { CaseAssetsTab } from "@/domains/cases/components/caseAssetsTab";
import { CaseHistoryTab } from "@/domains/cases/components/caseHistoryTab";
import { CaseTasksTab } from "@/domains/tasks";
import { CaseMailTab } from "@/domains/communication";

export function CaseDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: caseFile, isPending, isError } = useCase(id ?? "");

  if (isPending) return <LoadingState label="Dossier laden…" />;
  if (isError) return <ErrorMessage message="Kon dossier niet ophalen." />;
  if (!caseFile)
    return (
      <EmptyState
        title="Dossier niet gevonden"
        action={
          <VButton variant="outline" onClick={() => navigate("/cases")}>
            Terug naar lijst
          </VButton>
        }
      />
    );

  const requestNumber = caseFile.requestId.replace("req-", "");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/cases"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Terug naar dossiers
        </Link>
        <Link
          to={`/requests/${caseFile.requestId}`}
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          <ClipboardList className="size-4" aria-hidden />
          <span>
            Aangemaakt uit aanvraag{" "}
            <span className="font-semibold">#{requestNumber}</span>
          </span>
          <ArrowRight
            className="size-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>
      </div>

      <PageHeader
        title={`#${caseFile.number} — ${caseFile.title}`}
        subtitle={`${caseFile.address} · ${caseFile.startDate} → ${caseFile.endDate}`}
        badge={<CaseStatusBadge status={caseFile.status} />}
      />

      <VTabs defaultValue="overview">
        <VTabsList>
          <VTabsTrigger value="overview">
            <FileText className="size-4" aria-hidden />
            Overzicht
          </VTabsTrigger>
          <VTabsTrigger value="assets">
            <FileText className="size-4" aria-hidden />
            Assets ({caseFile.assetIds.length})
          </VTabsTrigger>
          <VTabsTrigger value="tasks">
            <CheckSquare className="size-4" aria-hidden />
            Taken
          </VTabsTrigger>
          <VTabsTrigger value="communication">
            <Mail className="size-4" aria-hidden />
            Communicatie
          </VTabsTrigger>
          <VTabsTrigger value="history">
            <History className="size-4" aria-hidden />
            Historiek
          </VTabsTrigger>
        </VTabsList>
        <VTabsContent value="overview">
          <CaseOverviewTab caseFile={caseFile} />
        </VTabsContent>
        <VTabsContent value="assets">
          <CaseAssetsTab caseFile={caseFile} />
        </VTabsContent>
        <VTabsContent value="tasks">
          <CaseTasksTab caseId={caseFile.id} startDate={caseFile.startDate} />
        </VTabsContent>
        <VTabsContent value="communication">
          <CaseMailTab caseId={caseFile.id} />
        </VTabsContent>
        <VTabsContent value="history">
          <CaseHistoryTab caseFile={caseFile} />
        </VTabsContent>
      </VTabs>
    </div>
  );
}
